import { useEffect, useState } from "react";
import { createVotingSession } from "../../api/voting";
import { editCategory } from "../../api/tournaments";
import { ICreateVotingSessionRequest, EligibleVoterType } from "../../models/IVoting";
import { Alert, Button, Checkbox, Dialog, Field, Select } from "../../ui";
import "./styles.scss";

type CreateVotingSessionDialogProps = {
  tournamentId: string;
  tournamentCategoryId?: string;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
  awards?: Array<{
    id: string;
    name: string;
    playerName: string;
    playerId: string;
    rankingPoints: number;
    includedInVoting: boolean;
  }>;
  categoryId?: string;
};

const CreateVotingSessionDialog = (props: CreateVotingSessionDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState<ICreateVotingSessionRequest>({
    tournamentId: props.tournamentId,
    tournamentCategoryId: props.tournamentCategoryId,
    eligibleVoterType: EligibleVoterType.AllUsers,
    allowSelfVote: false,
    allowSameTeamVote: false,
    selectedAwards: props.awards?.filter((award) => award.includedInVoting).map((award) => award.id) || [],
  });

  useEffect(() => {
    if (props.isOpen) {
      setFormData({
        tournamentId: props.tournamentId,
        tournamentCategoryId: props.tournamentCategoryId,
        eligibleVoterType: EligibleVoterType.AllUsers,
        allowSelfVote: false,
        allowSameTeamVote: false,
        selectedAwards: props.awards?.filter((award) => award.includedInVoting).map((award) => award.id) || [],
      });
    }
  }, [props.isOpen, props.tournamentId, props.tournamentCategoryId]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleInputChange = (field: keyof ICreateVotingSessionRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAwardToggle = (awardId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedAwards: prev.selectedAwards?.includes(awardId)
        ? prev.selectedAwards.filter((id) => id !== awardId)
        : [...(prev.selectedAwards || []), awardId],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      // Create the voting session
      await createVotingSession(formData);

      // Update awards includedInVoting status based on selected awards
      if (props.categoryId && props.awards) {
        const updatedAwards = props.awards.map((award) => ({
          ...award,
          includedInVoting: formData.selectedAwards?.includes(award.id) || false,
        }));

        await editCategory(props.categoryId, {
          awards: updatedAwards.map((award) => ({
            id: award.id,
            name: award.name,
            playerId: award.playerId,
            rankingPoints: award.rankingPoints,
            includedInVoting: award.includedInVoting,
            edit: true,
          })),
        });
      }

      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to create voting session");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title="Create Voting Session"
      subtitle="Set up voting rules for this category (only one session allowed per category)"
      busy={loading}
      bodyClassName="voting-session-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Create Session
          </Button>
        </>
      }
    >
      <Field label="Eligible Voters">
        {(id) => (
          <Select
            id={id}
            block
            value={formData.eligibleVoterType}
            onChange={(e) => handleInputChange("eligibleVoterType", Number(e.target.value))}
          >
            <option value={EligibleVoterType.AllUsers}>All Users</option>
            <option value={EligibleVoterType.ParticipantsOnly}>Participants Only</option>
          </Select>
        )}
      </Field>

      <Checkbox
        checked={!!formData.allowSelfVote}
        onChange={(checked) => handleInputChange("allowSelfVote", checked)}
        label="Allow self-voting"
        description="Users can vote for themselves"
      />

      <Checkbox
        checked={!!formData.allowSameTeamVote}
        onChange={(checked) => handleInputChange("allowSameTeamVote", checked)}
        label="Allow same-team voting"
        description="Users can vote for teammates"
      />

      {props.awards && props.awards.length > 0 && (
        <div className="voting-session-dialog__awards">
          <p className="voting-session-dialog__awards-label">Select Awards for Voting</p>
          <p className="voting-session-dialog__awards-description">Choose which awards should be included in this voting session</p>
          <div className="voting-session-dialog__awards-list">
            {props.awards.map((award) => (
              <Checkbox
                key={award.id}
                className="voting-session-dialog__award"
                checked={formData.selectedAwards?.includes(award.id) || false}
                onChange={() => handleAwardToggle(award.id)}
                label={award.name}
                description={`${award.rankingPoints} points`}
              />
            ))}
          </div>
        </div>
      )}

      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
    </Dialog>
  );
};

export default CreateVotingSessionDialog;
